import {
  enqueueSnackbar,
  type OptionsObject,
  type SnackbarMessage,
} from 'notistack';
import {TrackUbaAudit} from './analytics';

const OFFLINE_QUEUE_KEY = 'error_toast_audit_offline_queue';
const MAX_OFFLINE_QUEUE_SIZE = 50;

const LOG_TAG = 'ERROR_TOAST_AUDIT';

const generateErrorToastId = (): string => {
  const ts = Date.now();
  const rand = Math.random().toString(36).slice(2, 7).toUpperCase();
  return `ET-${ts}-${rand}`;
};

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

const istISOString = (): string =>
  new Date(Date.now() + IST_OFFSET_MS).toISOString().replace('Z', '+05:30');

interface ErrorToastLogEntry extends Record<string, unknown> {
  logTag: string;
  errorToastId: string;
  timestamp: string;
  toastErrorType: string;
  toastMessage: string;
  requestUrl: string;
  requestMethod: string;
  httpStatusCode: number;
  axiosErrorCode: string;
  responseErrorMessage: string;
  requestDurationMs: number | null;
  appVersion: string;
  pageUrl: string;
  pagePath: string;
  sessionUsername: string;
  connectionType: string;
  isOnline: boolean;
  traceid: string;
  referer: string;
  userid: string;
  ipAddress: string;
  t_ds: string;
}

const readOfflineQueue = (): ErrorToastLogEntry[] => {
  try {
    const raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
    return raw ? (JSON.parse(raw) as ErrorToastLogEntry[]) : [];
  } catch {
    return [];
  }
};

const writeOfflineQueue = (queue: ErrorToastLogEntry[]): void => {
  try {
    localStorage.setItem(
      OFFLINE_QUEUE_KEY,
      JSON.stringify(queue.slice(-MAX_OFFLINE_QUEUE_SIZE))
    );
  } catch {
    // localStorage may be full or unavailable (private browsing, storage quota)
  }
};

const getConnectionType = (): string => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const conn = (navigator as any).connection;
    return conn?.effectiveType ?? conn?.type ?? 'unknown';
  } catch {
    return 'unknown';
  }
};

declare global {
  interface Window {
    __T_DS__?: string;
  }
}

const getTds = (): string => {
  try {
    return window.__T_DS__ ?? '';
  } catch {
    return '';
  }
};

const buildEntry = (
  toastErrorType: string,
  toastMessage: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any
): ErrorToastLogEntry => ({
  logTag: LOG_TAG,
  errorToastId: generateErrorToastId(),
  timestamp: istISOString(),
  toastErrorType,
  toastMessage,
  requestUrl: String(error?.config?.url ?? ''),
  requestMethod: String(error?.config?.method ?? '').toUpperCase(),
  httpStatusCode: Number(error?.response?.status ?? 0),
  axiosErrorCode: String(error?.code ?? ''),
  responseErrorMessage: String(
    error?.response?.data?.error?.message ??
      error?.response?.data?.message ??
      ''
  ),
  requestDurationMs: error?.config?.metadata?.startTime
    ? Date.now() - error.config.metadata.startTime
    : null,
  appVersion: process.env.NEXT_PUBLIC_VERSION ?? '',
  pageUrl: window.location.href,
  pagePath: window.location.pathname,
  sessionUsername: sessionStorage.getItem('userId') ?? '',
  connectionType: getConnectionType(),
  isOnline: navigator.onLine,
  traceid: String(
    error?.response?.headers?.['x-trace-id'] ??
      error?.response?.headers?.traceid ??
      error?.response?.headers?.['trace-id'] ??
      ''
  ),
  referer: document.referrer ?? '',
  userid: sessionStorage.getItem('userId') ?? '',
  ipAddress: '',
  t_ds: getTds(),
});

const SNACKBAR_SUPPRESSED_PATHS = [
  '/talent-bridge-detail',
  '/talent-bridge-hiring-drive',
];

export const isSnackbarSuppressedPath = (): boolean => {
  try {
    const path = window.location.pathname;
    return SNACKBAR_SUPPRESSED_PATHS.some(
      suppressed => path === suppressed || path.startsWith(`${suppressed}/`)
    );
  } catch {
    return false;
  }
};

const flushOfflineQueue = (): void => {
  const queue = readOfflineQueue();
  if (queue.length === 0) return;
  localStorage.removeItem(OFFLINE_QUEUE_KEY);
  queue.forEach(entry => TrackUbaAudit('errorToastShown', entry));
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    setTimeout(flushOfflineQueue, 1500);
  });

  if (navigator.onLine) {
    setTimeout(flushOfflineQueue, 2000);
  }
}

/**
 * Call this whenever an error toast is shown to the user.
 * Logs the event via UBA audit immediately if online; queues it in
 * localStorage when offline so it is flushed once connectivity returns.
 *
 * @param toastErrorType  - stable key identifying the error category
 *                          e.g. 'offline' | 'network-timeout' | 'network-error' | 'server-error'
 * @param toastMessage    - the exact message string displayed in the toast
 * @param error           - the original Axios error object for request context
 */
export const logErrorToast = (
  toastErrorType: string,
  toastMessage: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any
): void => {
  if (typeof window === 'undefined') return;

  const entry = buildEntry(toastErrorType, toastMessage, error);

  if (!navigator.onLine) {
    const queue = readOfflineQueue();
    queue.push(entry);
    writeOfflineQueue(queue);
    return;
  }

  TrackUbaAudit('errorToastShown', entry);
};

/**
 * Show an error toast AND audit-log it once it actually renders on screen.
 *
 * Use this instead of `enqueueSnackbar(msg, {variant: 'error'})` for every
 * user-facing error toast. Logging is tied to notistack's `onEntered`
 * transition callback, so an entry is recorded only when the toast genuinely
 * appears (toasts dropped by page navigation / unload are not logged).
 *
 * @param message  - the exact message string displayed in the toast
 * @param error    - the original error/response object (Axios error, API
 *                   response, caught exception) so the full underlying reason
 *                   — status code, request URL, response body — is captured.
 *                   Omit for pure client-side validation messages.
 * @param options  - any extra notistack options (autoHideDuration, anchorOrigin,
 *                   persist, action, …). `variant` and `onEntered` are managed
 *                   internally and cannot be overridden.
 */
export const showErrorToast = (
  message: SnackbarMessage,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any,
  options?: Omit<OptionsObject, 'variant' | 'onEntered'>
): void => {
  const loggedMessage =
    typeof message === 'string' ? message : '[non-text-toast]';

  // On these pages, suppress the visible snackbar but still audit-log the error.
  if (isSnackbarSuppressedPath()) {
    logErrorToast('app-error', loggedMessage, error);
    return;
  }

  enqueueSnackbar(message, {
    ...options,
    variant: 'error',
    onEntered: () => logErrorToast('app-error', loggedMessage, error),
  });
};
